import React, { useState, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import ReactDatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { CalendarIcon } from '@heroicons/react/24/outline';
import 'react-datepicker/dist/react-datepicker.css';

// Custom CSS to ensure proper z-index and positioning
const datePickerStyles = `
  .react-datepicker-popper-custom {
    z-index: 9999 !important;
    position: absolute !important;
  }
  .react-datepicker-popper-custom .react-datepicker {
    position: relative !important;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined' && !document.getElementById('date-picker-styles')) {
  const style = document.createElement('style');
  style.id = 'date-picker-styles';
  style.textContent = datePickerStyles;
  document.head.appendChild(style);
}

interface DatePickerProps {
  label?: string;
  value?: string | Date | null;
  onChange?: (date: string | null) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  selectsRange?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  onRangeChange?: (dates: [Date | null, Date | null]) => void;
  usePortal?: boolean;
}

// Custom input component for the date picker
const CustomInput = forwardRef<HTMLInputElement, any>(
  ({ value, onClick, placeholder, label, error, required, hasValue, isFocused, onFocus, onBlur }, ref) => {
    const isLabelFloating = isFocused || hasValue;
    
    return (
      <div className="relative">
        <input
          ref={ref}
          value={value}
          onClick={onClick}
          onFocus={onFocus}
          onBlur={onBlur}
          readOnly
          placeholder=""
          className={`
            w-full px-4 py-3 text-base bg-white border rounded-lg transition-all duration-200 cursor-pointer
            focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            ${error 
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' 
              : 'border-gray-300 hover:border-gray-400'
            }
            ${hasValue ? 'text-gray-900' : 'text-gray-500'}
          `}
        />
        
        {label && (
          <label
            className={`
              absolute left-4 transition-all duration-200 pointer-events-none z-10
              ${isLabelFloating
                ? '-top-2.5 text-xs bg-white px-2'
                : 'top-1/2 -translate-y-1/2 text-base'
              }
              ${error && isLabelFloating ? 'text-red-500' : 
                isFocused && !error ? 'text-blue-500' : 'text-gray-500'
              }
            `}
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        
        <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
      </div>
    );
  }
);

CustomInput.displayName = 'CustomInput';

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  error,
  placeholder = 'Select date',
  required = false,
  disabled = false,
  className = '',
  selectsRange = false,
  startDate,
  endDate,
  onRangeChange,
  usePortal = true,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  // Convert string value to Date object
  const getDateValue = () => {
    if (!value) return null;
    if (typeof value === 'string') {
      return new Date(value);
    }
    return value;
  };

  const handleDateChange = (date: Date | null | [Date | null, Date | null]) => {
    if (selectsRange && Array.isArray(date)) {
      // Handle range selection
      if (onRangeChange) {
        onRangeChange(date);
      }
    } else if (!selectsRange && !Array.isArray(date)) {
      // Handle single date selection
      if (onChange) {
        onChange(date ? format(date, 'yyyy-MM-dd') : null);
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const dateValue = selectsRange ? startDate : getDateValue();
  const hasValue = Boolean(dateValue || (selectsRange && (startDate || endDate)));


  return (
    <div className={`w-full ${className}`}>
      {selectsRange ? (
        <ReactDatePicker
          onChange={handleDateChange}
          selectsRange={true}
          startDate={startDate}
          endDate={endDate}
          disabled={disabled}
          dateFormat="MMM dd, yyyy"
          placeholderText={placeholder}
          wrapperClassName="w-full"
          className="w-full"
          customInput={
            <CustomInput
              placeholder={placeholder}
              label={label}
              error={error}
              required={required}
              hasValue={hasValue}
              isFocused={isFocused}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          }
          popperContainer={usePortal ? ({ children }) => ReactDOM.createPortal(children, document.body) : undefined}
          popperClassName="react-datepicker-popper-custom"
          popperPlacement="bottom-start"
          showPopperArrow={false}
          calendarClassName="shadow-lg border border-gray-200 rounded-lg"
        />
      ) : (
        <ReactDatePicker
          selected={dateValue}
          onChange={handleDateChange}
          disabled={disabled}
          dateFormat="MMM dd, yyyy"
          placeholderText={placeholder}
          wrapperClassName="w-full"
          className="w-full"
          customInput={
            <CustomInput
              placeholder={placeholder}
              label={label}
              error={error}
              required={required}
              hasValue={hasValue}
              isFocused={isFocused}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          }
          popperContainer={usePortal ? ({ children }) => ReactDOM.createPortal(children, document.body) : undefined}
          popperClassName="react-datepicker-popper-custom"
          popperPlacement="bottom-start"
          showPopperArrow={false}
          calendarClassName="shadow-lg border border-gray-200 rounded-lg"
        />
      )}
      
      {error && (
        <div className="mt-2 text-sm text-red-600">
          {error}
        </div>
      )}
    </div>
  );
};

export default DatePicker;